/**
 * Loosely compares Map keys as long as they are compatible with `JSON.stringify`.
 *
 * Normally Map compares keys strictly, so this allows us to look up an object in a Map with
 * another object even if the object doesn't have the same memory reference.
 */
export class LooseMap extends Map {
    set(key, value) {
        return super.set(this.stringifyKey(key), value);
    }
    get(key) {
        return super.get(this.stringifyKey(key));
    }
    delete(key) {
        return super.delete(this.stringifyKey(key));
    }
    has(key) {
        return super.has(this.stringifyKey(key));
    }
    /**
     * Normalize and stringify key
     */
    stringifyKey(key) {
        return typeof key === 'string' ? key : JSON.stringify(this.deepSort(key));
    }
    /**
     * Normalize order of keys / elements with a stable sorting algorithm.
     *
     * This ensures it's input into the map with the same structure for comparisons.
     */
    deepSort(input) {
        // Early return for primitives
        if (typeof input !== 'object' || input === null) {
            return input;
        }
        /**
         * Recursively sort arrays
         *
         * **NOTE**: Currently doesn't support sorting the *order* of elements for arrays of objects,
         * but does sort the objects within each element of the array.
         */
        if (Array.isArray(input)) {
            return input.map(this.deepSort).sort();
        }
        // Recursively sort object keys
        return Object.keys(input)
            .sort()
            .reduce((sortedObj, key) => {
            const currValue = input[key];
            sortedObj[key] = this.deepSort(currValue);
            return sortedObj;
        }, {});
    }
}
